import React, { useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { SpaceCategory } from "@lifeat/shared/data-models/virtual-space/SpaceCategory";
import { VirtualSpace } from "@lifeat/shared/data-models/virtual-space/VirtualSpace";
import { SpaceSidebarTab } from "@lifeat/shared/spaces/SpacesUtil";
import { AmplitudeService } from "@analytics/Amplitude/AmplitudeService";
import { HorizontalDivider } from "@common/LifeAtDivider/LifeAtDivider";
import Tab from "@common/LifeAtTabs/Tab";
import Tabs from "@common/LifeAtTabs/Tabs";
import { LifeAtTextInputV2 } from "@common/LifeAtTextInput/LifeAtTextInputV2";
import { CallToActionContextType, useCallToActionContext } from "@contexts/CallToActionContext";
import { UserContextType, useUserContext } from "@contexts/UserContext";
import { useVirtualSpaceContext } from "@contexts/VirtualSpaceContext";
import { useDebounce } from "@hooks/useDebounce";
import { SignUpCTASource } from "@user/AmplitudeUserEvents";
import { SelectSpaceEvent } from "../../AmplitudeSpaceEvents";
import CurrentVirtualSpace from "../VirtualSpaceWidgets/CurrentVirtualSpace/CurrentVirtualSpace";
import SpaceShuffler from "../VirtualSpaceWidgets/SpaceShuffler/SpaceShuffler";
import DiscoverSpaces from "./DiscoverSpaces";
import FavoriteSpacesList from "./FavoriteSpacesList";
import SpaceThumbnail from "./SpaceThumbnail";

const StyledTabsContainer = styled(Tabs)`
    padding-top: 5px;
`;
const SearchInput = styled(LifeAtTextInputV2)`
    padding: 10px 10px 0 10px;
`;

const SpaceSidebarContent = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
`;

const SpaceResults = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    height: 100%;
    overflow-y: scroll;
    padding: 10px;
`;

const ContentContainer = styled.div`
    height: 100%;
    width: 100%;
    overflow: auto;
    display: flex;
    flex-direction: column;
`;

export const SpaceList = styled.div`
    display: flex;
    flex-wrap: wrap;
    width: 100%;
    gap: 10px;
`;

const SEARCH_BAR_PLACEHOLDER = "🔍 Search space";

export default function SpaceSelectionContent() {
    const { user, userData }: UserContextType = useUserContext();
    const { setSignInView }: CallToActionContextType = useCallToActionContext();
    const { spaceController } = useVirtualSpaceContext();
    const inputRef = useRef<HTMLInputElement | null>(null);
    const amplitudeService = AmplitudeService.getInstance();

    const [searchTermInputVal, setSearchTermInputVal] = useState<string>("");

    const [categoryFilter, setCategoryFilter] = useState<SpaceCategory | undefined>();
    const [searchFilter, setSearchFilter] = useState<string | undefined>();

    const discoverContentIsActive = !searchFilter && !categoryFilter;

    const handleSelectSearchedSpace = (space: VirtualSpace) => {
        spaceController.playSpace(space);
        amplitudeService.logEvent(
            new SelectSpaceEvent(
                {
                    spaceId: space.id,
                    spaceName: space.name,
                    spaceCategory: space.category,
                    spaceCreator: space.creatorName,
                },
                "search",
            ),
        );
    };

    const clearSearchFilter = () => {
        setSearchFilter(undefined);
        setSearchTermInputVal("");
    };

    const clearCategoryFilter = () => {
        setCategoryFilter(undefined);
    };

    const debounceSearchChange = useDebounce((searchTerm: string) => {
        setSearchFilter(searchTerm);
    }, 250);

    const handleSearchChange = (searchTerm: string) => {
        setSearchTermInputVal(searchTerm);
        debounceSearchChange(searchTerm);
        clearCategoryFilter();
    };

    const handleSelectCategory = (newCategory: SpaceCategory) => {
        spaceController.selectCategory(newCategory);
        setCategoryFilter(newCategory);
        clearSearchFilter();
    };

    const handleSelectDiscover = () => {
        clearCategoryFilter();
        clearSearchFilter();
    };

    const filteredSpaces = useMemo(() => {
        if (categoryFilter) {
            return spaceController.spacesByCategory.get(categoryFilter) ?? [];
        }
        if (searchFilter) {
            return spaceController.searchSpacesByText(searchFilter);
        }
        return [];
    }, [categoryFilter, searchFilter, spaceController]);

    const filteredSpacesContent = (
        <SpaceList>
            {filteredSpaces.map((space) => {
                return (
                    <SpaceThumbnail
                        key={space.id}
                        space={space}
                        handleSelectSpace={() => handleSelectSearchedSpace(space)}
                    />
                );
            })}
        </SpaceList>
    );

    return (
        <SpaceSidebarContent>
            <StyledTabsContainer>
                <Tab
                    label={SpaceSidebarTab.VIDEOS}
                    isActiveTab={spaceController.selectedTab === SpaceSidebarTab.VIDEOS}
                    handleSelect={() => spaceController.setActiveTab(SpaceSidebarTab.VIDEOS)}
                    buttonSize={"medium"}
                />
                <Tab
                    label={SpaceSidebarTab.IMAGES}
                    isActiveTab={spaceController.selectedTab === SpaceSidebarTab.IMAGES}
                    handleSelect={() => spaceController.setActiveTab(SpaceSidebarTab.IMAGES)}
                    buttonSize={"medium"}
                />
                <Tab
                    label={SpaceSidebarTab.FAVORITES}
                    isActiveTab={spaceController.selectedTab === SpaceSidebarTab.FAVORITES}
                    handleSelect={() => {
                        if (user && userData) {
                            spaceController.setActiveTab(SpaceSidebarTab.FAVORITES);
                        } else {
                            setSignInView(SignUpCTASource.SAVE_SPACE);
                        }
                    }}
                    buttonSize={"medium"}
                />
            </StyledTabsContainer>

            {(spaceController.selectedTab === SpaceSidebarTab.VIDEOS ||
                spaceController.selectedTab === SpaceSidebarTab.IMAGES) && (
                <ContentContainer>
                    <SearchInput
                        placeholder={SEARCH_BAR_PLACEHOLDER}
                        value={searchTermInputVal}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleSearchChange(e.target.value)}
                        onClick={() => inputRef.current?.focus()}
                        showBorder={true}
                        ref={inputRef}
                    />
                    <SpaceShuffler
                        onDiscoverButtonClick={handleSelectDiscover}
                        isDiscoverButtonActive={discoverContentIsActive}
                        selectedSpaceCategory={categoryFilter}
                        selectCategory={handleSelectCategory}
                    />
                    <HorizontalDivider widthCssValue={"100%"} />
                    <SpaceResults>{discoverContentIsActive ? <DiscoverSpaces /> : filteredSpacesContent}</SpaceResults>
                </ContentContainer>
            )}
            {spaceController.selectedTab === SpaceSidebarTab.FAVORITES && (
                <SpaceResults>
                    <FavoriteSpacesList />
                </SpaceResults>
            )}
            <CurrentVirtualSpace />
        </SpaceSidebarContent>
    );
}
